import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api, LEVEL_LABELS } from '@/lib/api';
import { Swords, Timer, FileQuestion, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ChallengeLandingPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/challenge/${challengeId}`)
      .then(r => { setChallenge(r.data); setLoading(false); })
      .catch(() => { toast.error('Challenge nicht gefunden'); navigate('/'); });
  }, [challengeId, navigate]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-[#9CA3AF] animate-pulse font-outfit">Lade Challenge...</div>
      </div>
    );
  }

  if (!challenge) return null;

  if (challenge.expired) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center animate-fade-up">
          <AlertTriangle className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="font-outfit font-bold text-2xl text-white mb-2">Challenge abgelaufen</h2>
          <p className="text-[#9CA3AF] mb-6">Diese Challenge ist leider nicht mehr gültig.</p>
          <Button onClick={() => navigate('/')} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="back-home-btn">
            Eigenes Quiz starten
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center px-4" data-testid="challenge-landing-page">
      <div className="glass-card rounded-2xl p-8 sm:p-10 max-w-md w-full text-center animate-fade-up">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mb-6">
          <Swords className="w-8 h-8 text-[#A78BFA]" />
        </div>

        <h2 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-2">Du wurdest herausgefordert!</h2>
        <p className="text-[#60A5FA] font-outfit font-bold text-lg mb-2">von {challenge.displayName}</p>

        {challenge.message && (
          <p className="text-[#9CA3AF] italic mb-6 bg-white/5 rounded-lg p-3 text-sm">
            &ldquo;{challenge.message}&rdquo;
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white/5 rounded-xl p-4">
            <FileQuestion className="w-5 h-5 text-[#3B82F6] mx-auto mb-1" />
            <p className="text-xl font-outfit font-bold text-white">{challenge.totalQuestions}</p>
            <p className="text-xs text-[#9CA3AF]">Fragen</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4">
            <Timer className="w-5 h-5 text-[#F59E0B] mx-auto mb-1" />
            <p className="text-xl font-outfit font-bold text-white">{challenge.timePerQuestionSeconds}s</p>
            <p className="text-xs text-[#9CA3AF]">pro Frage</p>
          </div>
        </div>

        <p className="text-xs text-[#4B5563] mb-6">
          Gleiche Fragen, gleiche Zeit - {LEVEL_LABELS[challenge.level]}
        </p>

        <Button
          onClick={() => navigate('/quiz/play', { state: { challengeId, level: challenge.level } })}
          className="w-full h-14 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white font-outfit font-bold text-lg rounded-xl transition-all hover:-translate-y-0.5"
          data-testid="start-challenge-btn"
        >
          Challenge starten
        </Button>
      </div>
    </div>
  );
}
